import type { ApiClient } from '../../api/client';
import type {
  WorkflowApprovalLabel,
  WorkflowApprovalLabelInput,
  WorkflowApprovalRoleOption,
  WorkflowApprovalSettingsPort,
  WorkflowApprovalTemplate,
  WorkflowApprovalTemplateInput,
} from '../../features/workflow/public';

type ItemsResponse<T> = { items: T[] };

function mutationKey(value: string | undefined): string {
  return value ?? crypto.randomUUID();
}

export function createWorkflowApprovalSettingsHttpAdapter(client: ApiClient): WorkflowApprovalSettingsPort {
  return {
    async loadSnapshot() {
      const [labels, templates] = await Promise.all([
        client.requestTenant<ItemsResponse<WorkflowApprovalLabel>>('/api/hr/v1/{tenant}/workflowApprovalLabels'),
        client.requestTenant<ItemsResponse<WorkflowApprovalTemplate>>('/api/hr/v1/{tenant}/workflowApprovalTemplates'),
      ]);
      return { labels: labels.items, templates: templates.items };
    },

    async listRoleOptions(query = '', limit = 20, includeUnassigned = false) {
      const params = new URLSearchParams({ limit: String(limit) });
      if (query.trim()) params.set('query', query.trim());
      if (includeUnassigned) params.set('includeUnassigned', 'true');
      const response = await client.requestTenant<ItemsResponse<WorkflowApprovalRoleOption>>(
        `/api/hr/v1/{tenant}/workflowApprovalRoleOptions?${params}`,
      );
      return response.items;
    },

    createLabel(input: WorkflowApprovalLabelInput, idempotencyKey?: string) {
      return client.requestTenant<WorkflowApprovalLabel>('/api/hr/v1/{tenant}/workflowApprovalLabels', {
        method: 'POST',
        headers: { 'Idempotency-Key': mutationKey(idempotencyKey) },
        body: JSON.stringify(input),
      });
    },

    updateLabel(id: string, input: WorkflowApprovalLabelInput, idempotencyKey?: string) {
      return client.requestTenant<WorkflowApprovalLabel>(`/api/hr/v1/{tenant}/workflowApprovalLabels/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Idempotency-Key': mutationKey(idempotencyKey) },
        body: JSON.stringify(input),
      });
    },

    deleteLabel(id: string, expectedVersion: number, idempotencyKey?: string) {
      const query = new URLSearchParams({ expectedVersion: String(expectedVersion) });
      return client.requestTenant<void>(`/api/hr/v1/{tenant}/workflowApprovalLabels/${encodeURIComponent(id)}?${query}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': mutationKey(idempotencyKey) },
      });
    },

    createTemplate(input: WorkflowApprovalTemplateInput, idempotencyKey?: string) {
      return client.requestTenant<WorkflowApprovalTemplate>('/api/hr/v1/{tenant}/workflowApprovalTemplates', {
        method: 'POST',
        headers: { 'Idempotency-Key': mutationKey(idempotencyKey) },
        body: JSON.stringify(input),
      });
    },

    updateTemplate(id: string, input: WorkflowApprovalTemplateInput, idempotencyKey?: string) {
      return client.requestTenant<WorkflowApprovalTemplate>(`/api/hr/v1/{tenant}/workflowApprovalTemplates/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Idempotency-Key': mutationKey(idempotencyKey) },
        body: JSON.stringify(input),
      });
    },

    deleteTemplate(id: string, expectedVersion: number, idempotencyKey?: string) {
      const query = new URLSearchParams({ expectedVersion: String(expectedVersion) });
      return client.requestTenant<void>(`/api/hr/v1/{tenant}/workflowApprovalTemplates/${encodeURIComponent(id)}?${query}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': mutationKey(idempotencyKey) },
      });
    },
  };
}
